import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const StudioEditorContext = createContext();

const extractStreetFromAddress = (address) => {
    if (!address) return "";
    const parts = String(address).split(',');
    return parts.length > 0 ? parts[0].trim() : "";
};

const getStreetFromStudio = (studio) => {
    if (studio?.streetAndNumber) {
        return studio.streetAndNumber;
    }
    if (studio?.address) {
        return extractStreetFromAddress(studio.address);
    }
    return "";
};

const extractStudioTags = (studio) => {
    const rawTags = studio?.tagValues ?? studio?.tags?.items ?? [];
    return rawTags
        .map((tag) => {
            if (typeof tag === "string") return tag;
            return tag?.value ?? tag?.tagValue ?? tag?.name ?? tag?.tag?.name ?? null;
        })
        .filter(Boolean);
};

const normalizeEquipmentMap = (equipment) => {
    if (!equipment || typeof equipment !== "object") {
        return {};
    }

    const normalized = {};
    const sourceEntries = Array.isArray(equipment?.items)
        ? equipment.items.map((entry) => [entry?.category, entry?.items])
        : Object.entries(equipment);

    sourceEntries.forEach(([category, items]) => {
        if (!category || !Array.isArray(items)) return;
        const validItems = items
            .filter((item) => item && typeof item === "object" && String(item.name || "").trim())
            .map((item) => ({
                id: String(item.id || ""),
                name: String(item.name || "").trim(),
                count: Math.max(1, Number(item.count || 1)),
            }))
            .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));

        if (validItems.length > 0) {
            normalized[category] = validItems;
        }
    });

    return normalized;
};

export const buildStudioEditorInitialValues = (studio) => {
    if (!studio) {
        return {};
    }

    return {
        StudioName: studio.StudioName || "",
        description: studio.description || "",
        streetAndNumber: getStreetFromStudio(studio),
        adressLine2: studio.adressLine2 || "",
        postcode: studio.postcode ?? "",
        city: studio.city || "",
        country: studio.country || "Deutschland",
        latitude: studio.latitude ?? null,
        longitude: studio.longitude ?? null,
        studioTypes: studio.studioTypes || [],
        tagValues: extractStudioTags(studio),
        studioSize: studio.studioSize ?? "",
        productionAreaSize: studio.productionAreaSize ?? "",
        numberOfPeople: studio.numberOfPeople ?? "",
        amenities: studio.amenities || [],
        equipment: normalizeEquipmentMap(studio.equipment),
        cancellationPolicy: studio.cancellationPolicy ?? null,
        houseRules: studio.houseRules ?? null,
        pricePerHour: studio.pricePerHour ?? null,
        minNumberOfHours: studio.minNumberOfHours ?? null,
        pricingMode: studio.pricingMode === undefined ? undefined : (studio.pricingMode || "HOURLY"),
        tieredPricing: studio.tieredPricing ?? null,
        packages: studio.packages ?? null,
        discountRules: studio.discountRules ?? null,
        weekendSurcharge: studio.weekendSurcharge ?? null,
        extraFees: studio.extraFees ?? null,
    };
};

const NUMERIC_FIELDS = new Set([
    "pricePerHour",
    "minNumberOfHours",
    "studioSize",
    "productionAreaSize",
    "numberOfPeople",
    "latitude",
    "longitude",
]);

const SET_ARRAY_FIELDS = new Set(["amenities", "studioTypes", "tagValues"]);
const NULL_WHEN_EMPTY_FIELDS = new Set(["tieredPricing", "packages", "discountRules", "extraFees"]);

const sortObjectKeys = (value) => {
    if (Array.isArray(value)) {
        return value.map(sortObjectKeys);
    }
    if (value && typeof value === "object") {
        return Object.keys(value)
            .filter((key) => key !== "__typename")
            .sort()
            .reduce((acc, key) => {
                acc[key] = sortObjectKeys(value[key]);
                return acc;
            }, {});
    }
    return value;
};

const normalizePricingArray = (fieldName, value) => {
    if (value == null || (Array.isArray(value) && value.length === 0)) {
        return null;
    }
    if (!Array.isArray(value)) {
        return sortObjectKeys(value);
    }

    const normalized = fieldName === "packages"
        ? value.map((pkg) => ({
            ...sortObjectKeys(pkg),
            prices: Array.isArray(pkg?.prices)
                ? pkg.prices
                    .map(sortObjectKeys)
                    .sort((a, b) =>
                        (a.minPersons ?? 0) - (b.minPersons ?? 0) ||
                        (a.maxPersons ?? 0) - (b.maxPersons ?? 0) ||
                        (a.price ?? 0) - (b.price ?? 0)
                    )
                : [],
        }))
        : value.map(sortObjectKeys);
    if (fieldName === "tieredPricing") {
        return normalized.sort((a, b) => (a.minPersons ?? 0) - (b.minPersons ?? 0));
    }
    if (fieldName === "packages") {
        return normalized.sort((a, b) => (a.durationHours ?? 0) - (b.durationHours ?? 0) || String(a.id || "").localeCompare(String(b.id || "")));
    }
    if (fieldName === "discountRules") {
        return normalized.sort((a, b) => (a.minHours ?? 0) - (b.minHours ?? 0));
    }
    if (fieldName === "extraFees") {
        return normalized.sort((a, b) => String(a.id || "").localeCompare(String(b.id || "")) || String(a.name || "").localeCompare(String(b.name || "")));
    }
    return normalized;
};

const normalizeWeekendSurcharge = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value !== "object") {
        return value;
    }

    const surcharge = sortObjectKeys(value);
    const saturdayEnabled = Boolean(surcharge.saturdayEnabled);
    const sundayEnabled = Boolean(surcharge.sundayEnabled);

    if (!saturdayEnabled && !sundayEnabled) {
        return null;
    }

    return {
        saturdayEnabled,
        saturdaySurchargeType: saturdayEnabled ? surcharge.saturdaySurchargeType ?? null : null,
        saturdaySurchargeValue: saturdayEnabled ? surcharge.saturdaySurchargeValue ?? null : null,
        sundayEnabled,
        sundaySurchargeType: sundayEnabled ? surcharge.sundaySurchargeType ?? null : null,
        sundaySurchargeValue: sundayEnabled ? surcharge.sundaySurchargeValue ?? null : null,
    };
};

export const normalizeEditorFieldValue = (fieldName, value) => {
    if (value === undefined) {
        return undefined;
    }

    if (NUMERIC_FIELDS.has(fieldName)) {
        if (value === "" || value === null) return value;
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : value;
    }

    if (SET_ARRAY_FIELDS.has(fieldName)) {
        return Array.isArray(value)
            ? [...value].map((item) => String(item)).sort()
            : [];
    }

    if (NULL_WHEN_EMPTY_FIELDS.has(fieldName)) {
        return normalizePricingArray(fieldName, value);
    }

    if (fieldName === "equipment") {
        return normalizeEquipmentMap(value);
    }

    if (fieldName === "weekendSurcharge") {
        return normalizeWeekendSurcharge(value);
    }

    return sortObjectKeys(value);
};

const valuesAreEqual = (fieldName, left, right) => (
    JSON.stringify(normalizeEditorFieldValue(fieldName, left)) ===
    JSON.stringify(normalizeEditorFieldValue(fieldName, right))
);

export const StudioEditorProvider = ({ children, studio }) => {
    const [updatedFields, setUpdatedFields] = useState({});
    const initialValues = useMemo(() => buildStudioEditorInitialValues(studio), [studio]);
    const initialValuesRef = useRef(initialValues);
    const studioIdRef = useRef(studio?.id);
    initialValuesRef.current = initialValues;

    useEffect(() => {
        if (studioIdRef.current === studio?.id) {
            return;
        }
        studioIdRef.current = studio?.id;
        setUpdatedFields({});
    }, [studio?.id]);

    useEffect(() => {
        setUpdatedFields(prev => {
            let changed = false;
            const next = {};

            Object.entries(prev).forEach(([fieldName, value]) => {
                if (value === undefined || valuesAreEqual(fieldName, value, initialValues[fieldName])) {
                    changed = true;
                    return;
                }
                next[fieldName] = value;
            });

            return changed ? next : prev;
        });
    }, [initialValues]);

    const updateField = useCallback((fieldName, data) => {
        setUpdatedFields(prev => {
            if (data === undefined || valuesAreEqual(fieldName, data, initialValuesRef.current[fieldName])) {
                const { [fieldName]: _removed, ...remainingFields } = prev;
                return remainingFields;
            }

            const newFields = {
                ...prev,
                [fieldName]: data
            };
            return newFields;
        });
    }, []);

    const clearUpdates = useCallback(() => {
        setUpdatedFields({});
    }, []);

    return (
        <StudioEditorContext.Provider value={{
            updatedFields,
            updateField,
            clearUpdates
        }}>
            {children}
        </StudioEditorContext.Provider>
    );
};

export const useStudioEditor = () => {
    const context = useContext(StudioEditorContext);
    if (!context) {
        // Return safe defaults when not in editor mode (e.g., in create flow)
        return {
            updatedFields: {},
            updateField: () => {},
            clearUpdates: () => {}
        };
    }
    return context;
};
